import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { GIButton, Modal } from 'app-components';
import { GIInput, InputSize } from '../../../components/common/app-components/GIInput';
import { logACall } from '../../../actions/account-activity/accountActivityActionCreators';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import {
    StyledDatePickerContainer,
    StyledError,
    StyledCustomDatePickerInput,
    StyledGrid,
    StyledInputContainer,
    StyledLabel,
    StyledModalButtonFooter,
    StyledModalGridItem,
} from './AccountActivity.styled';
import { DATE_FORMATS, formatDateString } from '../../../utils/dates/dateMethods';
import { useAppDispatch } from '../../../hooks';
import { BREAK_POINTS } from '../../../constants/common/breakpoints';
import { LOG_A_CALL } from './common/accountActivity.constants';
import { FormattedMessage } from 'react-intl';
import { useAccountActivityState } from '../../../hooks/useAccountActivityState';

interface ILogCallModalProps {
    isOpen: boolean;
    onClose: (isRefetchEvents: boolean) => void;
    consumerId: string;
}

const schema = z.object({
    duration: z.coerce.number().max(LOG_A_CALL.DURATION_MAX_VALUE).min(LOG_A_CALL.DURATION_MIN_VALUE),
    comments: z.string().max(LOG_A_CALL.COMMENTS_MAX_LENGTH),
    nextSteps: z.string().max(LOG_A_CALL.NEXT_STEPS_MAX_LENGTH),
});

const LogCallModal = ({ isOpen, onClose, consumerId }: ILogCallModalProps) => {
    const [isMobile, setIsMobile] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const dispatch = useAppDispatch();
    const { borderSize } = useAccountActivityState();

    const form = useForm({
        defaultValues: {
            duration: '',
            comments: '',
            nextSteps: '',
        },
        resolver: zodResolver(schema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    const {
        reset,
        getValues,
        formState: { errors },
    } = form;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= +BREAK_POINTS.md.split('px')[0]);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const popperPlacement = isMobile ? 'bottom' : 'right-end';

    const onClosePopup = (refetchData: boolean) => {
        reset();
        setSelectedDate(new Date());
        onClose(refetchData);
    };

    const onContinue = () => {
        if (!Object.keys(errors).length) {
            const data = getValues();
            const payload = {
                timeStamp: formatDateString(
                    selectedDate.toISOString()!,
                    DATE_FORMATS.US_DATE_TIME_FORMAT_SLASH_UPPERCASE,
                ),
                duration: Number(data.duration),
                notes: data.comments,
                nextSteps: data.nextSteps,
                consumerId,
            };

            const formData = new FormData();
            formData.append('callLog', JSON.stringify(payload));
            formData.append('csrftoken', window.csrf_token);

            dispatch(
                logACall(formData, () => {
                    onClosePopup(true);
                }),
            );
        }
    };

    return (
        <ConditionalRendering isTrue={isOpen}>
            <Modal data-testid="log-call-modal" id="log_a_call_modal">
                <Modal.Header
                    id="modal_header"
                    titleId="member.account.activity.log.a.call.title"
                    onHide={() => onClosePopup(false)}
                />
                <Modal.Body>
                    <StyledGrid columns={2}>
                        <StyledModalGridItem>
                            <StyledInputContainer>
                                <StyledLabel>
                                    <FormattedMessage id="member.account.activity.log.a.call.date.time" />
                                    <span>*</span>
                                </StyledLabel>
                                <StyledDatePickerContainer>
                                    <DatePicker
                                        data-testid="log_call_date"
                                        selected={selectedDate}
                                        showPopperArrow={false}
                                        onChangeRaw={e => e.preventDefault()}
                                        showTimeSelect
                                        timeFormat={DATE_FORMATS.HH_MM}
                                        timeIntervals={5}
                                        onChange={d => {
                                            if (d) {
                                                setSelectedDate(d);
                                            }
                                        }}
                                        showIcon
                                        popperPlacement={popperPlacement}
                                        dateFormat={DATE_FORMATS.US_DATE_TIME_FORMAT_SLASH_LOWERCASE}
                                        customInput={
                                            <StyledCustomDatePickerInput
                                                borderSize={borderSize}
                                                readOnly
                                                disabled
                                                type="text"
                                                id="log_call_date"
                                                onKeyDown={e => e.preventDefault()}
                                            />
                                        }
                                    />
                                </StyledDatePickerContainer>
                            </StyledInputContainer>
                        </StyledModalGridItem>
                        <StyledModalGridItem>
                            <StyledInputContainer>
                                <StyledLabel>
                                    <FormattedMessage id="member.account.activity.log.a.call.duration" />{' '}
                                </StyledLabel>
                                <GIInput
                                    id={`duration`}
                                    size={InputSize.MD}
                                    type="number"
                                    {...form.register('duration')}
                                    onKeyDown={e => {
                                        if (e.key === '.' || e.key === '-' || e.key === '+') {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                {errors.duration && (
                                    <StyledError>
                                        <FormattedMessage
                                            id="member.account.activity.log.a.call.maxLength.error"
                                            values={{
                                                minValue: LOG_A_CALL.DURATION_MIN_VALUE,
                                                maxValue: LOG_A_CALL.DURATION_MAX_VALUE
                                            }}
                                        />
                                    </StyledError>
                                )}
                            </StyledInputContainer>
                        </StyledModalGridItem>
                    </StyledGrid>
                    <StyledGrid columns={1}>
                        <StyledModalGridItem>
                            <StyledInputContainer>
                                <StyledLabel>
                                    <FormattedMessage id="member.account.activity.log.a.call.comments" />
                                </StyledLabel>
                                <textarea
                                    aria-required={false}
                                    data-testid={'comments'}
                                    maxLength={LOG_A_CALL.COMMENTS_MAX_LENGTH}
                                    disabled={false}
                                    id={'comments'}
                                    {...form.register('comments')}
                                />
                                {errors.comments && (
                                    <StyledError>
                                        <FormattedMessage
                                            id="member.account.activity.log.a.call.maxLength.error"
                                            values={{ maxLength: LOG_A_CALL.COMMENTS_MAX_LENGTH }}
                                        />
                                    </StyledError>
                                )}
                            </StyledInputContainer>
                        </StyledModalGridItem>
                    </StyledGrid>
                    <StyledGrid columns={1}>
                        <StyledModalGridItem>
                            <StyledInputContainer>
                                <StyledLabel>
                                    <FormattedMessage id="member.account.activity.log.a.call.next.steps" />
                                </StyledLabel>
                                <textarea
                                    aria-required={false}
                                    data-testid={'nextSteps'}
                                    maxLength={LOG_A_CALL.NEXT_STEPS_MAX_LENGTH}
                                    disabled={false}
                                    id={'nextSteps'}
                                    {...form.register('nextSteps')}
                                />
                                {errors.nextSteps && (
                                    <StyledError>
                                        <FormattedMessage
                                            id="member.account.activity.log.a.call.maxLength.error"
                                            values={{ maxLength: LOG_A_CALL.NEXT_STEPS_MAX_LENGTH }}
                                        />
                                    </StyledError>
                                )}
                            </StyledInputContainer>
                        </StyledModalGridItem>
                    </StyledGrid>
                </Modal.Body>
                <Modal.Controls>
                    <StyledModalButtonFooter>
                        <GIButton
                            id="cancel_btn"
                            labelId="member.account.activity.log.a.call.close"
                            variant="secondary"
                            onClickHandler={() => onClosePopup(false)}
                        />
                        <GIButton
                            id="continue_btn"
                            labelId="member.account.activity.log.a.call.save.call"
                            isDisabled={Object.keys(errors).length > 0}
                            onClickHandler={() => {
                                onContinue();
                            }}
                        />
                    </StyledModalButtonFooter>
                </Modal.Controls>
            </Modal>
        </ConditionalRendering>
    );
};

export default LogCallModal;
